import { useForm } from '@inertiajs/react'
import Seo from '../components/Seo'
import PackArtwork from '../components/PackArtwork'
import { useShared } from '../lib/hooks'
import type { Pack } from '../lib/types'

export default function FreeClaim({
  pack,
  already_subscribed,
}: {
  pack: Pack
  already_subscribed: boolean
}) {
  const { auth, errors } = useShared()
  const form = useForm({ newsletter: already_subscribed })

  function submit(e: React.FormEvent) {
    e.preventDefault()
    form.post(`/packs/${pack.slug}/claim`)
  }

  return (
    <>
      <Seo title={`Get ${pack.name} free`} noindex />

      <div className="mx-auto max-w-xl px-4 sm:px-6 py-14">
        <div className="mono-label mb-3">Free download</div>
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">Get your free pack</h1>

        {/* Pack summary */}
        <div className="mt-8 flex items-center gap-4 border border-line p-4">
          <div className="size-20 shrink-0 border border-line overflow-hidden">
            <PackArtwork pack={pack} size="mid" />
          </div>
          <div className="min-w-0">
            <div className="mono-label">{pack.type_label} · {pack.catalog_number}</div>
            <div className="font-medium mt-0.5">{pack.name}</div>
            <div className="mono-label !text-emerald-400 mt-1">Free</div>
          </div>
        </div>

        <form onSubmit={submit} className="mt-8">
          <div className="border border-line p-4 sm:p-5">
            <div className="mono-label mb-2">Your email</div>
            <div className="text-sm">{auth.user?.email}</div>
            <p className="mt-1 text-xs text-muted">We'll send your download confirmation here.</p>
          </div>

          <label className="mt-4 flex items-start gap-3 border border-line p-4 sm:p-5 cursor-pointer">
            <input
              type="checkbox"
              checked={form.data.newsletter}
              onChange={(e) => form.setData('newsletter', e.target.checked)}
              className="mt-0.5 size-4 shrink-0 rounded border-line-2 bg-ink-soft text-paper focus:ring-0"
            />
            <span className="text-sm leading-relaxed">
              <span className="font-medium">Subscribe to the Forma Sound newsletter</span>{' '}
              <span className="text-muted">(required to download this free pack).</span>
              <span className="block text-muted mt-1">
                We'll email you new packs, free presets, and the occasional deal. Unsubscribe anytime.
              </span>
            </span>
          </label>

          {errors?.newsletter && (
            <div className="mt-3 text-sm text-red-400">{errors.newsletter}</div>
          )}

          <button
            type="submit"
            disabled={form.processing || !form.data.newsletter}
            className="w-full mt-6 h-12 bg-paper text-ink text-sm font-semibold hover:opacity-90 transition disabled:opacity-50"
          >
            {form.processing ? 'Getting your pack…' : 'Get my free pack'}
          </button>
          <p className="mt-3 text-center text-xs text-muted">
            Instant download · re-download anytime from your account.
          </p>
        </form>
      </div>
    </>
  )
}
